'use client';

import { useState, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

interface DatePickerProps {
	value?: string; // YYYY-MM-DD
	onChange: (date: string | undefined) => void;
	minDate?: Date;
	disableWeekends?: boolean;
	error?: boolean;
	id?: string;
}

const DAY_NAMES = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const MONTH_NAMES = [
	'Januari',
	'Februari',
	'Maart',
	'April',
	'Mei',
	'Juni',
	'Juli',
	'Augustus',
	'September',
	'Oktober',
	'November',
	'December',
];

function toDateStr(d: Date): string {
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	
return `${year}-${month}-${day}`;
}

function isSameDay(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function DatePicker({
	value,
	onChange,
	minDate,
	disableWeekends = true,
	error = false,
	id,
}: DatePickerProps) {
	const today = useMemo(() => {
		const d = new Date();
		d.setHours(0, 0, 0, 0);
		
return d;
	}, []);

	const effectiveMinDate = minDate || today;

	// Calendar view state
	const [viewMonth, setViewMonth] = useState(() => {
		if (value) {
			const d = new Date(value);
			
return { year: d.getFullYear(), month: d.getMonth() };
		}
		
return { year: today.getFullYear(), month: today.getMonth() };
	});

	const [isOpen, setIsOpen] = useState(false);

	const selectedDate = useMemo(() => (value ? new Date(value) : null), [value]);

	// Generate calendar days for the current view
	const calendarDays = useMemo(() => {
		const { year, month } = viewMonth;
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);

		// Monday = 0, Sunday = 6 (ISO week)
		let startDow = firstDay.getDay() - 1;
		if (startDow < 0) startDow = 6;

		const days: Array<{
			date: Date;
			inMonth: boolean;
			isDisabled: boolean;
			isToday: boolean;
			isSelected: boolean;
			isWeekend: boolean;
		}> = [];

		// Previous month padding
		for (let i = startDow - 1; i >= 0; i--) {
			const d = new Date(year, month, -i);
			days.push({
				date: d,
				inMonth: false,
				isDisabled: true,
				isToday: false,
				isSelected: false,
				isWeekend: d.getDay() === 0 || d.getDay() === 6,
			});
		}

		// Current month
		for (let day = 1; day <= lastDay.getDate(); day++) {
			const d = new Date(year, month, day);
			const dow = d.getDay();
			const isWeekend = dow === 0 || dow === 6;
			const isPast = d < effectiveMinDate;
			const isDisabled = isPast || (disableWeekends && isWeekend);

			days.push({
				date: d,
				inMonth: true,
				isDisabled,
				isToday: isSameDay(d, today),
				isSelected: selectedDate ? isSameDay(d, selectedDate) : false,
				isWeekend,
			});
		}

		// Next month padding (fill to 6 rows of 7)
		const remaining = 42 - days.length;
		for (let i = 1; i <= remaining; i++) {
			const d = new Date(year, month + 1, i);
			days.push({
				date: d,
				inMonth: false,
				isDisabled: true,
				isToday: false,
				isSelected: false,
				isWeekend: d.getDay() === 0 || d.getDay() === 6,
			});
		}

		return days;
	}, [viewMonth, effectiveMinDate, disableWeekends, today, selectedDate]);

	const prevMonth = useCallback(() => {
		setViewMonth((prev) => {
			if (prev.month === 0) return { year: prev.year - 1, month: 11 };
			
return { ...prev, month: prev.month - 1 };
		});
	}, []);

	const nextMonth = useCallback(() => {
		setViewMonth((prev) => {
			if (prev.month === 11) return { year: prev.year + 1, month: 0 };
			
return { ...prev, month: prev.month + 1 };
		});
	}, []);

	const handleSelect = useCallback(
		(date: Date) => {
			onChange(toDateStr(date));
			setIsOpen(false);
		},
		[onChange],
	);

	const handleClear = useCallback(() => {
		onChange(undefined);
		setIsOpen(false);
	}, [onChange]);

	// Format selected date for display
	const displayValue = useMemo(() => {
		if (!selectedDate) return '';
		const formatter = new Intl.DateTimeFormat('nl-NL', {
			weekday: 'long',
			day: 'numeric',
			month: 'long',
			year: 'numeric',
		});
		
return formatter.format(selectedDate);
	}, [selectedDate]);

	const canGoPrev = viewMonth.year > today.getFullYear() || viewMonth.month > today.getMonth();

	return (
		<div className="relative" id={id}>
			{/* Trigger button */}
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className={`flex h-12 w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-lg ring-offset-background transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
					error ? 'border-red-500' : 'border-input'
				} ${selectedDate ? 'text-foreground' : 'text-muted-foreground'}`}
			>
				<span className="truncate">{displayValue || 'Kies een datum'}</span>
				<CalendarDays className="size-5 shrink-0 opacity-50" />
			</button>

			{/* Calendar dropdown */}
			{isOpen && (
				<>
					{/* Backdrop */}
					<div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

					{/* Calendar */}
					<div className="absolute left-0 top-full z-50 mt-1 w-full min-w-[300px] rounded-lg border border-gray-200 bg-white p-4 shadow-xl">
						{/* Header */}
						<div className="mb-3 flex items-center justify-between">
							<button
								type="button"
								onClick={prevMonth}
								disabled={!canGoPrev}
								className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
								aria-label="Vorige maand"
							>
								<ChevronLeft className="size-5" />
							</button>
							<span className="text-sm font-semibold text-gray-900">
								{MONTH_NAMES[viewMonth.month]} {viewMonth.year}
							</span>
							<button
								type="button"
								onClick={nextMonth}
								className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
								aria-label="Volgende maand"
							>
								<ChevronRight className="size-5" />
							</button>
						</div>

						{/* Day headers */}
						<div className="grid grid-cols-7 mb-1">
							{DAY_NAMES.map((name, i) => (
								<div
									key={name}
									className={`text-center text-xs font-medium py-1 ${
										i >= 5 ? 'text-gray-300' : 'text-gray-500'
									}`}
								>
									{name}
								</div>
							))}
						</div>

						{/* Days grid */}
						<div className="grid grid-cols-7 gap-0.5">
							{calendarDays.map((day, idx) => (
								<button
									key={idx}
									type="button"
									disabled={day.isDisabled}
									onClick={() => !day.isDisabled && handleSelect(day.date)}
									className={`
										relative h-9 w-full rounded-md text-sm font-medium transition-colors
										${!day.inMonth ? 'text-gray-200' : ''}
										${day.isDisabled && day.inMonth && day.isWeekend ? 'text-gray-300 bg-gray-50 cursor-not-allowed line-through' : ''}
										${day.isDisabled && day.inMonth && !day.isWeekend ? 'text-gray-300 cursor-not-allowed' : ''}
										${!day.isDisabled && !day.isSelected ? 'text-gray-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer' : ''}
										${day.isSelected ? 'bg-[#446084] text-white hover:bg-[#3a5270]' : ''}
										${day.isToday && !day.isSelected ? 'ring-1 ring-[#446084] ring-inset' : ''}
									`}
								>
									{day.date.getDate()}
								</button>
							))}
						</div>

						{/* Footer */}
						<div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
							<span className="text-xs text-gray-400 flex items-center gap-1.5">
								<span className="inline-block size-3 rounded bg-gray-50 border border-gray-200 line-through text-[8px] text-center leading-3">5</span>
								Weekend
							</span>
							{selectedDate && (
								<button
									type="button"
									onClick={handleClear}
									className="text-xs text-gray-500 hover:text-red-600 transition-colors"
								>
									Wissen
								</button>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
}
